import React, { useEffect, useRef, useState } from "react";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import {
  ChevronDown,
  CornerDownLeft,
  User,
  X,
  ArrowRight,
} from "react-feather";
import classNames from "classnames";
import { ROLE_ID } from "@/Enums/roles";
import { LabeledSelectAutocomplete } from "@/Components/Form";
import { Inertia } from "@inertiajs/inertia";
import { stringToInitials } from "@/Utilities/Utilities";
import { useKeypress } from "@/Utilities/Hooks";

export function Account({ user, asAgency = false }) {
  const organization = usePage().props?.organization?.data || {};

  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);

  const memberships = user.memberships
    .filter((m) => {
      if (user.is_super) {
        return (
          m.organization_name === null ||
          m.organization_role_id !== ROLE_ID.SUPER
        );
      } else {
        return (
          m.agency_id === null ||
          !user.memberships.some((item) => item.organization_id === m.agency_id)
        );
      }
    })
    .map((m) => {
      return {
        value: m.id,
        name: m.organization_name || (user.is_super ? "Admin" : undefined),
      };
    });

  useKeypress(
    "Escape",
    () => {
      if (!isOpen) {
        return;
      }
      setIsOpen(false);
    },
    [isOpen],
  );

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isOpen && ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isOpen]);

  const handleOrganizationChange = (val) => {
    if (window.location.pathname.match(/\d+/)) {
      Inertia.visit("/dashboard", {
        onFinish: () => {
          selectMembership(val);
        },
      });
    } else {
      selectMembership(val);
    }
  };

  const selectMembership = (val) => {
    Inertia.post(`/select-membership/${val}`, null, {
      preserveState: false,
      onFinish: () => {
        setIsOpen(false);
      },
    });
  };

  return (
    <div className="ml-auto hidden lg:block">
      {user && (
        <div className="relative z-[500] h-full" ref={ref}>
          <div
            className={classNames(
              "flex h-full cursor-pointer select-none items-center whitespace-nowrap px-4 py-2 pr-12",
              {
                "bg-typo": isOpen,
                "hover:bg-gray": !isOpen && !asAgency,
                "hover:bg-typo": !isOpen && asAgency,
              },
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            {!organization ||
              (organization.name === undefined && (
                <User
                  className="mr-2 inline rounded-full bg-primary p-2 text-white"
                  size={40}
                />
              ))}
            {organization.name !== undefined && (
              <span
                title={organization.name}
                className={classNames(
                  "mr-2 inline w-10 rounded-full p-2 text-center font-semibold",
                  {
                    "bg-primary text-white": !asAgency,
                    "bg-white text-primary": asAgency,
                  },
                )}
              >
                {stringToInitials(organization.name)}
              </span>
            )}

            <span
              className={`${isOpen ? "text-white" : asAgency ? "text-white" : "text-typo"}`}
            >
              {user.firstname}
            </span>

            {isOpen ? (
              <X
                color={`white`}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-typo transition-all"
              />
            ) : (
              <ChevronDown
                className={classNames(
                  "absolute right-4 top-1/2 -translate-y-1/2 transition-all",
                  {
                    "text-typo": !asAgency,
                    "text-white": asAgency,
                  },
                )}
              />
            )}
          </div>

          <div
            className={classNames(
              "absolute right-0 flex flex-col bg-typo p-8 shadow",
              {
                hidden: !isOpen,
                "w-60": user.memberships.length <= 1,
                "w-64": user.memberships.length > 1,
              },
            )}
          >
            {memberships.length > 1 && (
              <LabeledSelectAutocomplete
                name="membership_id"
                title="You're signed in as:"
                options={memberships}
                className="mb-4 px-3 text-white"
                defaultValue={user.membership_id}
                onSelect={(val) => handleOrganizationChange(val)}
              />
            )}

            <InertiaLink
              className="rounded-sm px-4 py-2 text-white hover:bg-black"
              href="/your-account"
              onClick={() => setIsOpen(false)}
            >
              Your account
            </InertiaLink>

            {(user.organization_role_id === ROLE_ID.OWNER ||
              ((user.organization_role_id === ROLE_ID.GLOBAL_REPORTER ||
                (user.organization_role_id === ROLE_ID.SUPER &&
                  organization?.id)) &&
                !organization.is_agency)) && (
              <>
                <InertiaLink
                  className="rounded-sm px-4 py-2 text-white hover:bg-black"
                  href="/users"
                  onClick={() => setIsOpen(false)}
                >
                  Manage users
                </InertiaLink>

                <InertiaLink
                  className="rounded-sm px-4 py-2 text-white hover:bg-black"
                  href="/company-settings"
                  onClick={() => setIsOpen(false)}
                >
                  Company settings
                </InertiaLink>
              </>
            )}

            {(user.organization_role_id === ROLE_ID.GLOBAL_REPORTER ||
              (user.organization_role_id === ROLE_ID.SUPER &&
                organization?.id)) &&
              organization.is_agency && (
                <>
                  <InertiaLink
                    className="rounded-sm px-4 py-2 text-white hover:bg-black"
                    href={route("agency.users")}
                    onClick={() => setIsOpen(false)}
                  >
                    Manage users
                  </InertiaLink>

                  <InertiaLink
                    className="rounded-sm px-4 py-2 text-white hover:bg-black"
                    href={route("agency.settings")}
                    onClick={() => setIsOpen(false)}
                  >
                    Company settings
                  </InertiaLink>
                </>
              )}

            <div className="mt-4 flex flex-col border-t-2 border-white pt-4">
              <InertiaLink
                method="post"
                as="button"
                className="relative px-4 py-2 text-left text-white hover:rounded-sm hover:bg-black"
                href="/logout"
              >
                Log out
                <ArrowRight
                  className={`absolute right-4 top-1/2 -translate-y-1/2`}
                />
              </InertiaLink>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
